/**
 * Reports → PPSR Register.
 *
 * Winnie, 18 September: "Need a PPSR log under the Report section alongside Debtors &
 * Creditors... A report to view and confirm that PPSR checks and discharges have actually
 * gone through — this matters more once discharge is a live API call rather than an internal
 * flag, since 'automatic' doesn't mean 'unfailable.'"
 *
 * One row per vehicle across all three stages, and the only action on it is the manual Retry
 * on a failed discharge. Deliberately manual: an automatic retry on a lodgement that is
 * broken at the register — the wrong VIN registered in the first place — would run forever
 * with nobody looking at it.
 *
 * Filters read "All", per the platform naming rule.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CHECK_LABEL,
  CheckStatus,
  DISCHARGE_LABEL,
  PpsrRegisterReport,
  loadPpsrRegisterReport,
  ppsrRegisterCsv,
} from '../../services/ppsrRegisterReportService'
import { DischargeStatus, retryDischarge } from '../../services/ppsrDischargeService'
import ReportRangeFilter from './ReportRangeFilter'
import { ALL_TIME, DateRange } from '../../lib/dateRange'
import { toast } from '../Toast'

const shortDate = (iso?: string | null) =>
  !iso ? '—' : new Date(iso).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })

const CHECK_TONE: Record<CheckStatus, string> = {
  Clear: 'ok',
  ExistingInterestFound: 'warn',
  NotChecked: 'quiet',
}

const DISCHARGE_TONE: Record<DischargeStatus, string> = {
  Discharged: 'ok',
  Failed: 'bad',
  Pending: 'warn',
  NotApplicable: 'quiet',
}

export default function PpsrRegisterTab() {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  return (
    <>
      <ReportRangeFilter value={range} onChange={setRange} />
      <PpsrRegisterBody key={`${range.from}|${range.to}`} range={range} />
    </>
  )
}

function PpsrRegisterBody({ range }: { range: DateRange }) {
  const [rep, setRep] = useState<PpsrRegisterReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [checkFilter, setCheckFilter] = useState<'' | CheckStatus>('')
  const [dischargeFilter, setDischargeFilter] = useState<'' | DischargeStatus>('')

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    loadPpsrRegisterReport(range)
      .then(setRep)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the PPSR register'))
      .finally(() => setLoading(false))
  }, [range])

  useEffect(load, [load])

  const rows = useMemo(() => {
    if (!rep) return []
    return rep.rows.filter(
      (r) =>
        (!checkFilter || r.checkStatus === checkFilter) &&
        (!dischargeFilter || r.dischargeStatus === dischargeFilter),
    )
  }, [rep, checkFilter, dischargeFilter])

  const retry = async (dealId: string) => {
    setBusyId(dealId)
    try {
      const outcome = await retryDischarge(dealId)
      if (outcome.discharged) toast.show('Discharged. ' + outcome.message)
      else toast.error(outcome.message)
      load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The retry could not be run.')
    } finally {
      setBusyId(null)
    }
  }

  const exportCsv = () => {
    const blob = new Blob(['﻿' + ppsrRegisterCsv(rows).replace(/\n/g, '\r\n')], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `DirectAuto_PPSR_Register_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return <div className="rep-loading">Loading the PPSR register…</div>
  if (error) return <div className="rep-alert error">{error}</div>
  if (!rep) return null

  return (
    <div className="rep-tabbody">
      <div className="rep-stats five">
        <div className="rep-stat">
          <span className="rep-stat-label">Checked this period</span>
          <span className="rep-stat-value">{rep.summary.checkedThisPeriod}</span>
          <span className="rep-stat-note">PPSR searches that returned an answer</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Existing interests found</span>
          <span className="rep-stat-value">{rep.summary.existingInterestsFound}</span>
          <span className="rep-stat-note">should be nil, or explainable</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Total lodgements</span>
          <span className="rep-stat-value">{rep.summary.totalLodgements}</span>
          <span className="rep-stat-note">registrations Direct Auto lodged</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Discharged</span>
          <span className="rep-stat-value">{rep.summary.successfullyDischarged}</span>
          <span className="rep-stat-note">confirmed by the register</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Failed — needs attention</span>
          <span className="rep-stat-value" style={rep.summary.failedDischarges ? { color: 'var(--red)' } : undefined}>
            {rep.summary.failedDischarges}
          </span>
          <span className="rep-stat-note">retry from the row below</span>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-h">
          <h3>PPSR Register</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              className="rep-select"
              value={checkFilter}
              onChange={(e) => setCheckFilter(e.target.value as '' | CheckStatus)}
            >
              <option value="">All Checks</option>
              <option value="Clear">Clear</option>
              <option value="ExistingInterestFound">Existing interest found</option>
              <option value="NotChecked">Not yet checked</option>
            </select>
            <select
              className="rep-select"
              value={dischargeFilter}
              onChange={(e) => setDischargeFilter(e.target.value as '' | DischargeStatus)}
            >
              <option value="">All Discharges</option>
              <option value="Discharged">Discharged</option>
              <option value="Pending">Pending</option>
              <option value="Failed">Failed</option>
              <option value="NotApplicable">N/A</option>
            </select>
            <button className="rep-btn" onClick={exportCsv}>Export CSV</button>
          </div>
        </div>

        <div className="rep-table-wrap">
          <table className="rep-table">
            <thead>
              <tr>
                <th>VIN</th>
                <th>Vehicle</th>
                <th>Sold</th>
                <th>Payment cleared</th>
                <th>Check</th>
                <th>Registration</th>
                <th>Discharge</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.dealId}>
                  <td className="rep-mono">{r.vin || '—'}</td>
                  <td>
                    {r.vehicle}
                    {r.stockNumber ? <div className="rep-muted">#{r.stockNumber}</div> : null}
                  </td>
                  <td>{shortDate(r.soldDate)}</td>
                  <td>{shortDate(r.paymentClearedDate)}</td>
                  <td>
                    <span className={`ppsr-badge ${CHECK_TONE[r.checkStatus]}`}>{CHECK_LABEL[r.checkStatus]}</span>
                    <div className="rep-muted">{shortDate(r.checkedAt)}</div>
                  </td>
                  <td>
                    <span className={`ppsr-badge ${r.lodgedAt ? 'ok' : 'quiet'}`}>
                      {r.lodgedAt ? 'Lodged' : 'None'}
                    </span>
                    {r.lodgedAt ? (
                      <div className="rep-muted">
                        {shortDate(r.lodgedAt)}
                        {r.registrationNumber ? ` · ${r.registrationNumber}` : ''}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`ppsr-badge ${DISCHARGE_TONE[r.dischargeStatus]}`}>
                      {DISCHARGE_LABEL[r.dischargeStatus]}
                    </span>
                    {r.dischargedAt ? (
                      <div className="rep-muted">
                        {shortDate(r.dischargedAt)}
                        {r.dischargeReference ? ` · ${r.dischargeReference}` : ''}
                      </div>
                    ) : null}
                    {/* The register's own words. Without them a retry is a guess. */}
                    {r.dischargeStatus === 'Failed' && r.dischargeError ? (
                      <div className="ppsr-error">{r.dischargeError}</div>
                    ) : null}
                  </td>
                  <td>
                    {r.dischargeStatus === 'Failed' ? (
                      <button className="rep-btn" disabled={busyId === r.dealId} onClick={() => retry(r.dealId)}>
                        {busyId === r.dealId ? 'Retrying…' : 'Retry'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="rep-muted">
                    {rep.rows.length === 0
                      ? 'No vehicles yet.'
                      : 'No vehicles match these filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
